const parseNumber = (value) => {
  if (value === undefined || value === null) return 0;
  const cleaned = value.toString().replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

/** Monday string mm.dd.yyyy + 0–4 day offset → YYYY-MM-DD */
export function weekDayDateKeyFromMonday(mondayStr, dayIndex) {
  const parts = (mondayStr || '').split('.');
  if (parts.length !== 3) return null;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return null;
  const d = new Date(yyyy, mm - 1, dd + dayIndex);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthKeyFromDateKey(dateKey) {
  const p = (dateKey || '').split('-');
  if (p.length !== 3) return null;
  return `${p[1]}.${p[0]}`;
}

/**
 * @param {Array} rows
 * @param {object} [opts]
 * @param {(ctx: { dateKey: string, weekdayLabel: string, value: number }) => boolean} [opts.includeDay]
 */
export function computeStrategySheetMetrics(rows = [], opts = {}) {
  const includeDay = typeof opts.includeDay === 'function' ? opts.includeDay : null;
  if (includeDay) {
    return computeFiltered(rows, includeDay);
  }
  return computeUnfiltered(rows);
}

function computeUnfiltered(rows) {
  const headerRow = rows[1] || [];
  const idxMonday = headerRow.findIndex((c) => c?.toLowerCase() === 'monday');
  const idxTuesday = headerRow.findIndex((c) => c?.toLowerCase() === 'tuesday');
  const idxWednesday = headerRow.findIndex((c) => c?.toLowerCase() === 'wednesday');
  const idxThursday = headerRow.findIndex((c) => c?.toLowerCase() === 'thursday');
  const idxFriday = headerRow.findIndex((c) => c?.toLowerCase() === 'friday');

  const mondayCol = idxMonday >= 0 ? idxMonday : 1;
  const tuesdayCol = idxTuesday >= 0 ? idxTuesday : 2;
  const wednesdayCol = idxWednesday >= 0 ? idxWednesday : 3;
  const thursdayCol = idxThursday >= 0 ? idxThursday : 4;
  const fridayCol = idxFriday >= 0 ? idxFriday : 5;

  const dayCols = [mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol];

  const dataRows = rows.slice(2);
  let bestDayVal = -Infinity;
  let bestDay = null;
  let worstDayVal = Infinity;
  let worstDay = null;
  let bestWeekVal = -Infinity;
  let bestWeekDate = null;
  let worstWeekVal = Infinity;
  let worstWeekDate = null;
  let winningWeeks = 0;
  let losingWeeks = 0;
  const monthTotals = new Map();
  const weekdayWinCounts = {
    Mon: { wins: 0, total: 0 },
    Tue: { wins: 0, total: 0 },
    Wed: { wins: 0, total: 0 },
    Thu: { wins: 0, total: 0 },
    Fri: { wins: 0, total: 0 },
  };

  dataRows.forEach((row) => {
    const date = row[0];
    const days = dayCols.map((colIdx) => parseNumber(row[colIdx]));

    const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    labels.forEach((label, idx) => {
      const val = days[idx] ?? 0;
      if (val !== 0) {
        weekdayWinCounts[label].total += 1;
        if (val > 0) weekdayWinCounts[label].wins += 1;
      }
    });

    const total = days.reduce((a, b) => a + b, 0);

    if (date) {
      const parts = date.split('.');
      if (parts.length === 3) {
        const mm = parts[0];
        const yyyy = parts[2];
        const monthKey = `${mm}.${yyyy}`;
        monthTotals.set(monthKey, (monthTotals.get(monthKey) || 0) + total);
      }
    }

    labels.forEach((label, idx) => {
      const val = days[idx] ?? 0;
      if (val > bestDayVal) {
        bestDayVal = val;
        bestDay = { label, value: val, date };
      }
      if (val < worstDayVal) {
        worstDayVal = val;
        worstDay = { label, value: val, date };
      }
    });

    if (total > bestWeekVal) {
      bestWeekVal = total;
      bestWeekDate = date;
    }
    if (total < worstWeekVal) {
      worstWeekVal = total;
      worstWeekDate = date;
    }
    if (total > 0) winningWeeks += 1;
    if (total < 0) losingWeeks += 1;
  });

  let bestMonth = null;
  let worstMonth = null;
  monthTotals.forEach((sum, key) => {
    if (!bestMonth || sum > bestMonth.value) bestMonth = { month: key, value: sum };
    if (!worstMonth || sum < worstMonth.value) worstMonth = { month: key, value: sum };
  });

  let totalWinsSum = 0;
  let totalLossSum = 0;
  dataRows.forEach((row) => {
    const days = dayCols.map((colIdx) => parseNumber(row[colIdx]));
    days.forEach((val) => {
      if (val > 0) totalWinsSum += val;
      if (val < 0) totalLossSum += val;
    });
  });

  const weekdayWinRates = {};
  let totalWinningDays = 0;
  let totalDays = 0;
  Object.entries(weekdayWinCounts).forEach(([label, stats]) => {
    const pct = stats.total === 0 ? 0 : (stats.wins / stats.total) * 100;
    weekdayWinRates[label] = Math.round(pct * 10) / 10;
    totalWinningDays += stats.wins;
    totalDays += stats.total;
  });

  return {
    bestDay,
    worstDay,
    bestWeek: { date: bestWeekDate, value: bestWeekVal },
    worstWeek: { date: worstWeekDate, value: worstWeekVal },
    winningWeeks,
    losingWeeks,
    bestMonth,
    worstMonth,
    weekdayWinRates,
    totalWinsSum,
    totalLossSum,
    totalWinningDays,
    totalDays,
  };
}

function computeFiltered(rows, includeDay) {
  const headerRow = rows[1] || [];
  const idxMonday = headerRow.findIndex((c) => c?.toLowerCase() === 'monday');
  const idxTuesday = headerRow.findIndex((c) => c?.toLowerCase() === 'tuesday');
  const idxWednesday = headerRow.findIndex((c) => c?.toLowerCase() === 'wednesday');
  const idxThursday = headerRow.findIndex((c) => c?.toLowerCase() === 'thursday');
  const idxFriday = headerRow.findIndex((c) => c?.toLowerCase() === 'friday');

  const mondayCol = idxMonday >= 0 ? idxMonday : 1;
  const tuesdayCol = idxTuesday >= 0 ? idxTuesday : 2;
  const wednesdayCol = idxWednesday >= 0 ? idxWednesday : 3;
  const thursdayCol = idxThursday >= 0 ? idxThursday : 4;
  const fridayCol = idxFriday >= 0 ? idxFriday : 5;

  const dayCols = [mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol];
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

  const dataRows = rows.slice(2);
  let bestDayVal = -Infinity;
  let bestDay = null;
  let worstDayVal = Infinity;
  let worstDay = null;
  let bestWeekVal = -Infinity;
  let bestWeekDate = null;
  let worstWeekVal = Infinity;
  let worstWeekDate = null;
  let winningWeeks = 0;
  let losingWeeks = 0;
  const monthTotals = new Map();
  const weekdayWinCounts = {
    Mon: { wins: 0, total: 0 },
    Tue: { wins: 0, total: 0 },
    Wed: { wins: 0, total: 0 },
    Thu: { wins: 0, total: 0 },
    Fri: { wins: 0, total: 0 },
  };

  dataRows.forEach((row) => {
    const date = row[0];
    const days = dayCols.map((colIdx) => parseNumber(row[colIdx]));

    let weekFiltered = 0;
    labels.forEach((label, idx) => {
      const val = days[idx] ?? 0;
      const dateKey = weekDayDateKeyFromMonday(date, idx);
      if (!dateKey || !includeDay({ dateKey, weekdayLabel: label, value: val })) {
        return;
      }
      weekFiltered += val;

      if (val !== 0) {
        weekdayWinCounts[label].total += 1;
        if (val > 0) weekdayWinCounts[label].wins += 1;
      }

      const mk = monthKeyFromDateKey(dateKey);
      if (mk) {
        monthTotals.set(mk, (monthTotals.get(mk) || 0) + val);
      }

      if (val > bestDayVal) {
        bestDayVal = val;
        bestDay = { label, value: val, date };
      }
      if (val < worstDayVal) {
        worstDayVal = val;
        worstDay = { label, value: val, date };
      }
    });

    if (weekFiltered > bestWeekVal) {
      bestWeekVal = weekFiltered;
      bestWeekDate = date;
    }
    if (weekFiltered < worstWeekVal) {
      worstWeekVal = weekFiltered;
      worstWeekDate = date;
    }
    if (weekFiltered > 0) winningWeeks += 1;
    if (weekFiltered < 0) losingWeeks += 1;
  });

  let bestMonth = null;
  let worstMonth = null;
  monthTotals.forEach((sum, key) => {
    if (!bestMonth || sum > bestMonth.value) bestMonth = { month: key, value: sum };
    if (!worstMonth || sum < worstMonth.value) worstMonth = { month: key, value: sum };
  });

  let totalWinsSum = 0;
  let totalLossSum = 0;
  dataRows.forEach((row) => {
    const date = row[0];
    const days = dayCols.map((colIdx) => parseNumber(row[colIdx]));
    labels.forEach((label, idx) => {
      const val = days[idx] ?? 0;
      const dateKey = weekDayDateKeyFromMonday(date, idx);
      if (!dateKey || !includeDay({ dateKey, weekdayLabel: label, value: val })) return;
      if (val > 0) totalWinsSum += val;
      if (val < 0) totalLossSum += val;
    });
  });

  const weekdayWinRates = {};
  let totalWinningDays = 0;
  let totalDays = 0;
  Object.entries(weekdayWinCounts).forEach(([label, stats]) => {
    const pct = stats.total === 0 ? 0 : (stats.wins / stats.total) * 100;
    weekdayWinRates[label] = Math.round(pct * 10) / 10;
    totalWinningDays += stats.wins;
    totalDays += stats.total;
  });

  return {
    bestDay,
    worstDay,
    bestWeek: { date: bestWeekDate, value: bestWeekVal },
    worstWeek: { date: worstWeekDate, value: worstWeekVal },
    winningWeeks,
    losingWeeks,
    bestMonth,
    worstMonth,
    weekdayWinRates,
    totalWinsSum,
    totalLossSum,
    totalWinningDays,
    totalDays,
  };
}
